import React, { useCallback, useMemo } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { Link, useHistory, useLocation } from 'react-router-dom'
import block from 'bem-cn'

import { MODAL_ROUTE_URLS } from 'router/modalRoutes'
import { ROUTE_PATHS, ROUTE_URLS } from 'router/routes'

import { ReactComponent as Logo } from 'shared/img/logo.svg'

import { LocaleCommonKeysType } from 'shared/locale/en-US'
import { BonusPromotionStatus } from 'shared/models/Promotion'
import { useMediaMaxTablet } from 'shared/style/var'

import { selectIsUserAuthenticated } from 'features/auth/selectors'
import { selectLocaleDict } from 'features/locale/selectors'

import { useModalContext } from '@bcraft-frontend-lib/core'

import { INavigationItem, navigationItems } from 'components/NavigationHeader/data'
import { addNotify } from 'components/Notify'
import { SetLanguage } from 'components/SetLanguage/SetLanguage'
import { DropdownVerticalPosition, Icon } from 'components/UIKit'
import { Button, ButtonSizes, ButtonVariants } from 'components/UIKit/Button/Button'

import { WalletBlock } from '../components'
import { ReactComponent as CloseIcon } from '../img/close.svg'

import './SideMenuLeftContent.scss'

const b = block('side-menu-left-content')

export const SideMenuLeftContent: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const modal = useModalContext()
  const { pathname, search } = useLocation()
  const history = useHistory()
  const isMobileOrTablet = useMediaMaxTablet()

  const locale = useSelector(selectLocaleDict, shallowEqual).common
  const localeAuth = useSelector(selectLocaleDict, shallowEqual).auth
  const localeSlots = useSelector(selectLocaleDict, shallowEqual).slots
  const isAuth = useSelector(selectIsUserAuthenticated, shallowEqual)

  const onClickNavItem = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, item: INavigationItem) => {
      if (!isAuth && item.isAuthReq) {
        e.preventDefault()
        addNotify(localeSlots.pleaseAuth)
        onClose()
        return
      }
      if (item.modalRoute && (!item.isModalForMobile || (isMobileOrTablet && item.isModalForMobile))) {
        e.preventDefault()
        modal.pushNewModal(item.modalRoute)()
      }
      onClose()
    },
    [isAuth, isMobileOrTablet, localeSlots.pleaseAuth, modal, onClose],
  )

  const items = useMemo(
    () =>
      navigationItems
        .filter(it => (it.isAuthReq ? isAuth : true))
        .map(item => {
          const isActive = pathname === item.route || !!item.subItems?.find(it => `${pathname}${search}` === it)
          const IconComponent = item.svg
          return (
            <Link
              key={item.route + (item.modalRoute ?? '') + (item.mobileRoute ?? '')}
              to={isMobileOrTablet ? item.mobileRoute ?? item.route : item.route}
              className={b('menu-item', { isActive })}
              onClick={e => onClickNavItem(e, item)}>
              {IconComponent && <IconComponent className={b('menu-item-icon')} />}
              <span className={b('menu-item-text')}>{locale[item.text as LocaleCommonKeysType] ?? item.text}</span>
            </Link>
          )
        }),
    [locale, onClickNavItem, pathname, search, isAuth, isMobileOrTablet],
  )

  return (
    <div className={b()}>
      <div className={b('header')}>
        <Link className={b('logo-wrapper')} to={ROUTE_URLS.MAIN()}>
          <Logo className={b('logo')} />
        </Link>

        <button onClick={onClose} type="button" className={b('header-close')} aria-label="Close menu">
          <CloseIcon />
        </button>
      </div>

      <div className={b('body')}>
        {!isAuth && (
          <div className={b('header-login-wrapper')}>
            <Button
              size={ButtonSizes.MEDIUM}
              variant={ButtonVariants.SECONDARY}
              className={b('header-login-button')}
              onClick={() => {
                onClose()
                modal.pushNewModal(MODAL_ROUTE_URLS.SIGN_UP())()
              }}
              text={localeAuth.signUp}
            />
            <Button
              size={ButtonSizes.MEDIUM}
              className={b('header-login-button')}
              onClick={() => {
                onClose()
                modal.pushNewModal(MODAL_ROUTE_URLS.SIGN_IN())()
              }}
              text={locale.logIn}
            />
          </div>
        )}
        {/* {isAuth && <WalletBlock onClose={onClose} className={b('wallet')} />} */}

        <div className={b('items')}>
          {items}
          {/* {isAuth && (
            <>
              <div
                className={b('menu-item')}
                onClick={() => {
                  onClose()
                  history.push(ROUTE_PATHS.PROFILE_USERINFO)
                }}>
                <span className={b('menu-item-text')}>{locale.profile}</span>
              </div>
              <div
                className={b('menu-item')}
                onClick={() => {
                  onClose()
                  history.push(ROUTE_URLS.PROFILE_PROMOTIONS_STATUS(BonusPromotionStatus.active))
                }}>
                <span className={b('menu-item-text')}>{locale.promotions}</span>
              </div>
            </>
          )} */}
        </div>

        <div className={b('lang-wrapper')}>
          <SetLanguage dropdownPosition="top" dropdownAlign="left" />
        </div>
      </div>
    </div>
  )
}
